/**
 * Settings → Image Management: the API, and the storage behind it.
 *
 * ── Nothing new on purpose ───────────────────────────────────────────────────────────────
 *
 * Uploads go through Amplify Storage exactly as certification documents and receipts already do:
 * a uuid in the key so two files called `banner.png` cannot overwrite each other, the `public/`
 * prefix stripped before Amplify re-adds it, and a signed URL derived per view rather than stored.
 * See services/certificationDocuments for the same three rules written out at length.
 *
 * ── Why every call goes to a Lambda ──────────────────────────────────────────────────────
 *
 * OrgConfig is where organisation settings live, and this list is not in it. Its resolvers are
 * direct DynamoDB writes with no authorisation, on an API that carries an API_KEY provider whose
 * key ships inside this bundle — anything written through `putOrgConfig` can be written by anybody
 * who opens devtools. What appears on the company home page is not something to leave there, so
 * the records sit behind a function that checks the caller's role against the user pool.
 *
 * Which means: the Super Admin checks in this file and in the page are for the UI's benefit only.
 * They decide which buttons are worth rendering. They are not the security boundary, and removing
 * them would not grant anybody anything.
 */

import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import {
  ImagePlacement,
  ResolvedWebsiteImage,
  WebsiteImage,
  WebsiteImageInput,
} from '../types/websiteImage';

/** Formats a browser can decode. The same list the Lambda enforces. */
export const ALLOWED_IMAGE_TYPES = ['png', 'jpg', 'jpeg', 'webp'];

/**
 * The most a single image may weigh.
 *
 * 5MB rather than the 10MB certificates get: a certificate is evidence somebody has to be able to
 * read, and this is decoration that every employee downloads on every visit to the home page.
 */
export const MAX_IMAGE_SIZE_MB = 5;

/** How long a generated display URL stays valid, in seconds. */
export const IMAGE_URL_TTL_SECONDS = 3600;

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const IMAGE_FIELDS = `
  id
  placement
  name
  description
  storageKey
  sourcePath
  displayOrder
  active
  createdAt
  createdBy
  updatedAt
  updatedBy
`;

const listQuery = /* GraphQL */ `
  query ListWebsiteImages($placement: String!) {
    listWebsiteImages(placement: $placement) { ${IMAGE_FIELDS} }
  }
`;

const putMutation = /* GraphQL */ `
  mutation PutWebsiteImage($input: WebsiteImageInput!) {
    putWebsiteImage(input: $input) { ${IMAGE_FIELDS} }
  }
`;

const deleteMutation = /* GraphQL */ `
  mutation DeleteWebsiteImage($id: ID!) {
    deleteWebsiteImage(id: $id) { id }
  }
`;

const reorderMutation = /* GraphQL */ `
  mutation ReorderWebsiteImages($placement: String!, $ids: [ID!]!) {
    reorderWebsiteImages(placement: $placement, ids: $ids) { ${IMAGE_FIELDS} }
  }
`;

// ─── Storage ──────────────────────────────────────────────────────────────────────────────

/** Amplify v6 re-adds `public/`; a stored key carrying one would resolve to `public/public/…`. */
export function normalizeStorageKey(key: string): string {
  const trimmed = String(key || '').trim();
  return trimmed.startsWith('public/') ? trimmed.slice('public/'.length) : trimmed;
}

/** The key an uploaded image is stored under. A uuid, so two `banner.png` cannot collide. */
export function buildImageKey(placement: string, fileName: string, unique = uuidv4()): string {
  const safePlacement = String(placement || 'general').replace(/[^\w-]+/g, '_');
  const safeName = String(fileName || 'image').replace(/[^\w.-]+/g, '_');
  return `website-images/${safePlacement}/${unique}-${safeName}`;
}

/** Client-side validation. The same limits the form shows, so the two cannot disagree. */
export function validateImageFile(file: File): string | null {
  const parts = file.name.split('.');
  const ext = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  if (!ALLOWED_IMAGE_TYPES.includes(ext)) {
    return `"${ext || 'That file'}" is not an allowed image. Use ${ALLOWED_IMAGE_TYPES.join(', ').toUpperCase()}.`;
  }
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    return `That image is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is ${MAX_IMAGE_SIZE_MB}MB.`;
  }
  return null;
}

/** Upload an image and return the key to store on the record. */
export async function uploadImage(file: File, placement: string): Promise<string> {
  const problem = validateImageFile(file);
  if (problem) throw new Error(problem);

  const key = buildImageKey(placement, file.name);
  await uploadData({ key, data: file, options: { contentType: file.type } }).result;
  return key;
}

/**
 * The URL that renders one record, or '' when there is nothing to render.
 *
 * A built-in's `sourcePath` is returned as it is — it is a file the frontend build serves, and
 * signing a path that is not in the bucket would only fail. An uploaded key is signed for, without
 * `validateObjectExistence`: an `<img>` that fails already has an onError, so the probe costs a
 * HeadObject per image on a page that shows dozens and changes nothing on screen. It is also the
 * request the bucket's CORS is most likely to refuse — see the long note in certificationDocuments.
 */
export async function resolveImageUrl(image: WebsiteImage): Promise<string> {
  if (image.sourcePath) return image.sourcePath;
  const key = normalizeStorageKey(image.storageKey || '');
  if (!key) return '';
  try {
    const result = await getUrl({ key, options: { expiresIn: IMAGE_URL_TTL_SECONDS } });
    return result.url.toString();
  } catch (error) {
    console.warn('[WebsiteImages] Could not build a URL for', key, error);
    return '';
  }
}

/** Every record with its URL resolved. A record whose URL fails keeps its place, with url: ''. */
export async function resolveImageUrls(images: WebsiteImage[]): Promise<ResolvedWebsiteImage[]> {
  return Promise.all(
    images.map(async image => ({ ...image, url: await resolveImageUrl(image) }))
  );
}

// ─── The API ──────────────────────────────────────────────────────────────────────────────

/**
 * Turn a GraphQL failure into a sentence.
 *
 * The Lambda's own refusals — "Unauthorized: …", "That image no longer exists." — are already
 * written for a reader, so they are passed through. Everything else is a network or schema problem
 * the reader can do nothing about, and gets the caller's fallback instead of a stack of GraphQL.
 */
export function describeImageError(error: any, fallback: string): string {
  const graphQlMessage = error?.errors?.[0]?.message || error?.message || '';
  if (/unauthor|no longer exists|not an allowed|needs a|not in this placement|lists an image twice/i.test(graphQlMessage)) {
    return graphQlMessage;
  }
  /*
   * The API does not know these fields yet.
   *
   * AppSync answers a query for a field its schema has never heard of with a validation error —
   * "Validation error of type FieldUndefined: Field 'listWebsiteImages' in type 'Query' is
   * undefined" — which is a deployment fact, not a fault with any image. It used to fall through
   * to the caller's fallback, so a Super Admin opening the page before the Terraform had been
   * applied read "The configured images could not be loaded" and had no way to tell that from a
   * real outage. Saying which half is missing is the difference between a five-minute answer and
   * an afternoon.
   */
  if (/FieldUndefined|is undefined|Unknown type|Validation error|not configured/i.test(graphQlMessage)) {
    return 'Image management is not deployed in this environment yet. The Terraform for it (the WebsiteImages table, the website-images Lambda and its four AppSync resolvers) has to be applied before this page can load or save anything.';
  }
  if (/cannot return null|Unknown placement/i.test(graphQlMessage)) {
    return 'Image management is not available in this environment yet.';
  }
  return fallback;
}

export const WebsiteImageService = {
  /**
   * Every image for a placement, in display order, including the disabled ones.
   *
   * The settings page needs to show a Super Admin what they have hidden, so filtering to active
   * is the caller's job — the carousel does it, this does not.
   */
  async list(placement: ImagePlacement | string): Promise<WebsiteImage[]> {
    const response: any = await getClient().graphql({
      query: listQuery,
      variables: { placement },
    });
    const rows: WebsiteImage[] = response?.data?.listWebsiteImages || [];
    return [...rows].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  },

  /** Create or update. Super Admin only — enforced in the Lambda, not here. */
  async save(input: WebsiteImageInput): Promise<WebsiteImage> {
    const response: any = await getClient().graphql({
      query: putMutation,
      variables: { input },
    });
    return response?.data?.putWebsiteImage;
  },

  /**
   * Remove a record.
   *
   * The uploaded file is deliberately left in the bucket. Deleting it would be irreversible from a
   * screen whose confirm dialog says "remove this image from the site", and an orphaned object
   * costs pennies where a wrongly deleted photograph is gone. Bucket lifecycle is where that
   * belongs if it ever matters.
   */
  async remove(id: string): Promise<void> {
    await getClient().graphql({ query: deleteMutation, variables: { id } });
  },

  /** Persist a whole placement's order. Sends every id, because a drag moves everything after it. */
  async reorder(placement: ImagePlacement | string, ids: string[]): Promise<WebsiteImage[]> {
    const response: any = await getClient().graphql({
      query: reorderMutation,
      variables: { placement, ids },
    });
    return response?.data?.reorderWebsiteImages || [];
  },
};
